import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loginCheck } from "../../auth/auth";
import axiosClient from "../../libs/axiosClient";
import styles from "./TreatmentList.module.scss";
import HeaderMain from "../../components/HeaderMain/HeaderMain";
import HeaderMobile from "../../components/HeaderMobile/HeaderMobile";
import Footer from "../../components/Footer/Footer";
import detailStar from '../../assets/hospitalReservation/detailStar.png';

function TreatmentList() {
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState("consultation");
  const [selectedCategory, setSelectedCategory] = useState<
    '전체보기' | '성형외과' | '피부과'
  >(location.state?.category || '전체보기');
  const [treatments, setTreatments] = useState<{
    전체보기: any[];
    성형외과: any[];
    피부과: any[];
    // 한방: any[];
  }>({
    전체보기: [],
    성형외과: [],
    피부과: [],
    // 한방: [],
  });
  const [ratings, setRatings] = useState<Record<number, { avg_score: number; total_review_quantity: number }>>({});
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (!loginCheck()) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage") || "en";
    
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    const fetchData = async () => {
      try {
        const response = await axiosClient.get(
          "/api/v1/product",
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const data = response.data;
        const mappedData = {
          전체보기: data || [],
          성형외과: data.filter(
            (item: any) => item.medical_department_id === 1
          ),
          피부과: data.filter((item: any) => item.medical_department_id === 2),
          // 한방: data.filter((item: any) => item.medical_department_id === 3),
        };

        setTreatments(mappedData);
        
        const ratingsData: Record<number, { avg_score: number; total_review_quantity: number }> = {};
        
        for (const item of data) {
          const ratingResponse = await axiosClient.get(
            `/api/v1/review/${item.hospital_id}/stats`
          );
          ratingsData[item.hospital_id] = {
            avg_score: ratingResponse.data.avg_score,
            total_review_quantity: ratingResponse.data.total_review_quantity,
          };
        }
        
        setRatings(ratingsData);
      } catch (error) {
        console.error("Failed to fetch treatment data:", error);
      }
    };
    fetchData();
    
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [i18n]);

  const handleCategoryChange = (
    category: "전체보기" | "성형외과" | "피부과"
  ) => {
    setSelectedCategory(category);
  };

  const renderTreatmentCards = () => {
    const mapLanguageToDisplay = (language: string) => {
      switch (language.toLowerCase()) {
        case "한국어":
          return "한국어";
        case "영어":
          return "English";
        case "일본어":
          return "日本語";
        case "중국어":
          return "中文";
        case "아랍어":
          return "العربية";
        default:
          return language;
      }
    };
    
    return treatments[selectedCategory].map((treatment) => {
      const rating = ratings[treatment.hospital_id];
      return (
        <div
          key={treatment.id}
          className={styles.card}
          onClick={() => navigate(`/treatment-detail/${treatment.id}`)}
        >
          <div className={styles.image}>
          <img
            src={
              treatment.profile_image?.[0] ||
              treatment.banner_image?.[0] ||
              "/placeholder.png"
            }
            alt={treatment.title}
          />
          </div>
          <div className={styles.info}>
            <h3>{treatment.title}</h3>
            {/* <div className={styles.ratingSection}>
              <img src={detailStar} alt="Rating" className={styles.icon} />
              {rating && (
                <span className={styles.ratingText}>
                  {rating.avg_score.toFixed(1)} ({rating.total_review_quantity})
                </span>
              )}
            </div> */}
            {/* <div className={styles.price}>
              {treatment.price > 0 ? (
                <>
                  {treatment.price}{" "}
                  {treatment.is_vat_include && <span>VAT Included</span>}
                </>
              ) : (
                ""
              )}
            </div> */}
            <div className={styles.hospital}>
              {treatment.hospital?.name} | {treatment.hospital?.nearest_subway_station}
            </div>
            <div className={styles.languages}>
              {treatment.languages?.map((language: any) => (
                <span key={language.id} className={styles.languageTag}>
                  {mapLanguageToDisplay(language.title)}
                </span>
              ))}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <>
      {isMobile ? (
        <HeaderMobile />
      ) : (<HeaderMain selectedTab={selectedTab} onTabChange={setSelectedTab} />  
      )}
      <div className={styles.page}>
        <aside className={styles.sidebar}>
          <h2>
            {i18n
              .t("treatmentList.header")
              .split("\n")
              .map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
          </h2>

          <ul className={styles.categoryList}>
            {(["전체보기", "성형외과", "피부과"] as const).map(
              (category) => {
                const categoryKey = {
                  전체보기: "all",
                  성형외과: "plasticSurgery",
                  피부과: "dermatology",
                }[category];

                return (
                  <li
                    key={category}
                    className={
                      selectedCategory === category ? styles.active : ""
                    }
                    onClick={() => handleCategoryChange(category)}
                  >
                    {i18n.t(`treatmentList.categories.${categoryKey}`)}
                  </li>
                );
              }
            )}
          </ul>
        </aside>
        <main className={styles.content}>
          <div className={styles.grid}>{renderTreatmentCards()}</div>
        </main>
      </div>
      <Footer />
    </>
  );
}

export default TreatmentList;
